import { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { useParticipationPersonnel } from "@/api/hooks/participations";
import type { LocationPersonnel } from "@/api/types";
import { PersonnelDetailDialog } from "./PersonnelDetailDialog";

interface Props {
  participationId: string;
  /** Skip the fetch until truthy — lets callers defer until the user
   * actually expands the section. */
  enabled?: boolean;
  /** Optional location context for the per-person detail modal —
   * unlocks the role-history block. Doc-status loads either way. */
  locationId?: string | null;
}

function displayName(p: LocationPersonnel): string {
  return p.first_name ? `${p.first_name} ${p.last_name}` : p.last_name;
}

/** Sort key that pulls PIs to the top, then Sub-Is, then everyone else.
 * Mirrors the backend's canonicalize_personnel_role tokenization but
 * only for the two roles we care about ordering — everything else falls
 * through to alphabetical by last name. */
function roleRank(roleLabel: string | null | undefined): number {
  const norm = (roleLabel ?? "").trim().toLowerCase().replace(/[^a-z]/g, "");
  if (norm === "pi" || norm === "principalinvestigator") return 0;
  if (norm === "subi" || norm === "subinvestigator") return 1;
  return 2;
}

function comparePersonnel(a: LocationPersonnel, b: LocationPersonnel): number {
  const r = roleRank(a.role_label) - roleRank(b.role_label);
  if (r !== 0) return r;
  return (a.last_name ?? "").localeCompare(b.last_name ?? "");
}

function fmtDate(d: string | null): string {
  return d ?? "—";
}

function SignedCell({ value }: { value: boolean }) {
  return value ? (
    <CheckCircle fontSize="small" color="success" />
  ) : (
    <RadioButtonUnchecked fontSize="small" sx={{ color: "text.disabled" }} />
  );
}

/**
 * Read-only roster table for a Participation. Sourced from
 * LocationPersonnel rows written by the delegation-log review flow.
 * Renders an empty state when no roster has been confirmed yet.
 */
export function LocationPersonnelTable({
  participationId, enabled = true, locationId = null,
}: Props) {
  const query = useParticipationPersonnel(participationId, { enabled });
  const [selected, setSelected] = useState<LocationPersonnel | null>(null);

  if (!enabled) return null;
  if (query.isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }
  if (query.error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Could not load personnel:{" "}
        {query.error instanceof Error ? query.error.message : String(query.error)}
      </Alert>
    );
  }
  const rows = [...(query.data ?? [])].sort(comparePersonnel);
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1 }}>
        No personnel confirmed yet. Upload a delegation log against this
        participation to populate the roster.
      </Typography>
    );
  }

  return (
    <>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Delegated tasks</TableCell>
          <TableCell>Valid from</TableCell>
          <TableCell>Valid to</TableCell>
          <TableCell align="center">Signed</TableCell>
          <TableCell align="center">PI signed</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((p) => (
          <TableRow key={p.id} hover>
            <TableCell sx={{ fontWeight: 500 }}>
              <Link
                component="button"
                type="button"
                onClick={() => setSelected(p)}
                sx={{ textAlign: "left", fontWeight: 500 }}
              >
                {displayName(p)}
              </Link>
            </TableCell>
            <TableCell>{p.role_label}</TableCell>
            <TableCell>
              {p.delegated_tasks.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ) : (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {p.delegated_tasks.map((name, i) => {
                    const code = p.delegated_task_codes[i];
                    return (
                      <Tooltip
                        key={`${name}-${i}`}
                        title={code ? `Code ${code}` : ""}
                      >
                        <Chip size="small" label={name} variant="outlined" />
                      </Tooltip>
                    );
                  })}
                </Stack>
              )}
            </TableCell>
            <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
              {fmtDate(p.start_date)}
            </TableCell>
            <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
              {fmtDate(p.end_date)}
            </TableCell>
            <TableCell align="center">
              <SignedCell value={p.signed} />
            </TableCell>
            <TableCell align="center">
              <SignedCell value={p.pi_signed} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <PersonnelDetailDialog
      open={selected !== null}
      onClose={() => setSelected(null)}
      personnel={selected}
      locationId={locationId}
    />
    </>
  );
}
